import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { MdCategory } from 'react-icons/md'

import { useTranslation } from 'react-i18next'
import { fetchAnnouncess } from '../../services/announces'
import { fetchPractices } from '../../services/practices'

interface PracticeCount {
  practiceName: string
  practiceId: number
  count: number
  path: string
}

interface Announce {
  id: number
  practices: number[]
}

const PopularPractices: React.FC = () => {
  const { t } = useTranslation()
  const [popularPractices, setPopularPractices] = useState<PracticeCount[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const truncateText = (text: string, maxLength: number = 50) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
  }

  const handlePracticeClick = (practiceId: number) => {
    const defaultFilters = {
      gender: [],
      distance: ['< 10km'],
      location: [],
      orientation: [],
      ageRange: [],
      nationality: [],
      ethnicity: [],
      status: [],
      weightRange: [],
      sizeRange: [],
      hairColor: [],
      eyeColor: [],
      intimateHairCut: [],
      languages: [],
      penisSize: [],
      braCup: [],
      tattoo: null,
      smoker: null,
      piercing: null,
      massageOnly: null,
      alcohol: null,
      isVerified: null,
      practices: [practiceId],
    }

    localStorage.setItem('bigSearchFilters', JSON.stringify(defaultFilters))
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [announcesData, practiceCategories] = await Promise.all([
          fetchAnnouncess(),
          fetchPractices(),
        ])

        let announces: Announce[] = []
        if (Array.isArray(announcesData)) {
          announces = announcesData
        }

        const practiceMap = new Map<number, string>()
        practiceCategories.forEach((category: { practices: any[] }) => {
          category.practices.forEach(
            (practice: { id: number; practiceName: string }) => {
              practiceMap.set(practice.id, practice.practiceName)
            },
          )
        })

        const practiceCountMap = new Map<
          string,
          { id: number; count: number }
        >()

        announces.forEach((announce: Announce) => {
          if (Array.isArray(announce.practices)) {
            announce.practices.forEach(practiceId => {
              const practiceName = practiceMap.get(practiceId)
              if (practiceName) {
                const current = practiceCountMap.get(practiceName) || {
                  id: practiceId,
                  count: 0,
                }
                practiceCountMap.set(practiceName, {
                  id: practiceId,
                  count: current.count + 1,
                })
              }
            })
          }
        })

        const sortedPractices = Array.from(practiceCountMap.entries())
          .map(([practiceName, data]) => ({
            practiceName,
            practiceId: data.id,
            count: data.count,
            path: `/search/`,
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 7)

        setPopularPractices(sortedPractices)
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  if (isLoading) {
    return (
      <div className='lg:w-full text-center xl:text-left'>
        <h3 className='text-base flex font-semibold mb-4 pb-2 border-b-2 border-redpink text-white text-center xl:text-left'>
          <MdCategory className='mr-3 text-xl text-redpink text-center xl:text-left' />
          {t('popular-practices')}
        </h3>
        <div className='animate-pulse space-y-2'>
          {[...Array(7)].map((_, index) => (
            <div
              key={index}
              className='h-6 bg-anthraciteLight rounded w-full'
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className='lg:w-full text-center xl:text-left'>
      <h3 className='text-base flex font-semibold mb-4 pb-2 border-b-2 border-redpink text-white justify-center xl:justify-start'>
        <MdCategory className='mr-3 text-xl text-redpink text-center xl:text-left' />
        {t('popular-practices')}
      </h3>
      <ul className='xl:space-y-0'>
        {popularPractices.map(practice => (
          <li key={practice.practiceName}>
            <Link
              to={practice.path}
              onClick={() => {
                handlePracticeClick(practice.practiceId)
                window.location.href = '/search'
                window.scrollTo(0, 0)
              }}
              className='text-center xl:text-left text-gray-300 rounded hover:text-redpink hover:bg-anthraciteLight transition-all items-center'
            >
              <h2
                className='text-gray-300 block rounded py-1 hover:text-redpink hover:pl-2 hover:bg-anthraciteLight transition-all'
                title={t(`practices.${practice.practiceName}`)}
              >
                {truncateText(t(`practices.${practice.practiceName}`))}
              </h2>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default PopularPractices
